import { useState, useEffect, useRef } from 'react';
import {
  detectIdentifierType,
  validateIdentifierList,
  validateTypeConsistency,
} from '../../../utils/validations';
import { CombinedValidation } from '../types';

interface UseEnrichmentValidationProps {
  text: string;
  searchType: string;
  geneFilterType: string;
}

export const useEnrichmentValidation = ({
  text,
  searchType,
  geneFilterType,
}: UseEnrichmentValidationProps) => {
  const [validation, setValidation] = useState<CombinedValidation | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const searchTypeRef = useRef(searchType);

  useEffect(() => {
    searchTypeRef.current = searchType;
  }, [searchType]);

  useEffect(() => {
    if (text.trim()) {
      validateInput();
    } else {
      setValidation(null);
      setShowValidation(false);
    }
  }, [text, searchType, geneFilterType]);

  const validateInput = () => {
    if (!text.trim()) {
      setValidation(null);
      setShowValidation(false);
      return;
    }

    const filterType = searchTypeRef.current === 'GENES' ? geneFilterType : 'strID';

    const detectedType = detectIdentifierType(text);
    const validationResult = validateIdentifierList(text, filterType);
    const consistencyResult = validateTypeConsistency(text, filterType, searchTypeRef.current);

    const combinedValidation: CombinedValidation = {
      ...validationResult,
      ...consistencyResult,
      detectedType,
    };

    if (!consistencyResult.isConsistent && consistencyResult.detectedType) {
      (combinedValidation as any).suggestion = `Click here to switch to ${consistencyResult.detectedType}`;
    }

    setValidation(combinedValidation);
    setShowValidation(true);
  };

  const clearValidation = () => {
    setValidation(null);
    setShowValidation(false);
  };

  return {
    validation,
    showValidation,
    validateInput,
    clearValidation,
  };
};
